// src/app/App.tsx
import { useState, useEffect, useCallback, useRef } from "react";
import { LoginPage }        from "./components/login/login-page";
import { SignupPage }       from "./components/login/signup-page";
import { MfaSetupPage }     from "./components/login/mfa-setup-page";
import { MfaVerifyPage }    from "./components/login/mfa-verify-page";
import { ForceSetPasswordPage } from "./components/login/force-set-password-page";
import { UpmodeHome }       from "./components/brand/upmode-home";
import { AntennaDashboard } from "./components/antenna/antenna-dashboard";
import { CognitionAnalysis } from "./components/cognition";
import FixActionsPage       from "./components/fix-actions/fix-actions-page";
import { AISandboxPage }    from "./components/simulation/ai-sandbox-page";
import { DeepSignalPage }   from "./components/deep-signal";
import { AnalyticsOverview }from "./components/analytics/analytics-overview";
import { BrandVoiceProfile }from "./components/brand/brand-voice-profile";
import { SettingsPage }     from "./components/settings-page";
import { DataUploadsPage }  from "./components/data-uploads-page";
import { PromptTrackingPage}from "./components/prompt-tracking/prompt-tracking-page";
import { IntegrationsPage } from "./components/integrations-page";
import { NavRail }          from "./components/navigation/rail/nav-rail";
import { useOpenActionsCount } from "./hooks/nav/use-open-actions-count";
import { featureForPage }   from "./components/navigation/nav-config";
import { setPageTitle, moduleLabelForPage } from "./lib/page-title";
import { useEntitlements, stateOf } from "./components/navigation/use-entitlements";
import { SiteHeader }       from "./components/navigation/site-header";
import { ErrorBoundary }    from "./components/error-boundary";
import { hasSsoCallbackParams, completeSsoCallback } from "@/api/sso";
import { ThemeProvider }    from "./contexts/theme-context";
import { Toaster }          from "./components/shared/toaster";
import { AuthProvider }     from "./contexts/auth-context";
import { TermsOfService }   from "./components/login/terms-of-service";
import { PrivacyPolicy }    from "./components/login/privacy-policy";
import { AdminUsersPage }   from "./components/login/admin/admin-users-page";
import { AgentsPage }       from "./components/agents/agents-page";
import { AgentRunsPage }    from "./components/agents/agent-runs-page";
import { UpmodeAgentPage }  from "./components/upmode-agent/upmode-agent-page";
import { InsightsReportsPage } from "./components/reporting";
import { OnboardingPage }   from "./components/onboarding/onboarding-page";

import { getSettings }            from "./services/common/settings-service";
import { applyUserSettingsToDom } from "./components/helper/user-settings-apply";
import { useResponsive }          from "./hooks/common/use-breakpoint";
import { AUTH_EXPIRED_EVENT }     from "@/api/api-fetch";
import { authApi }                from "@/api/auth";

import { BrandPublisherPage }      from "./components/publishing/brand-publisher-page";
import { PublicationHistoryPage }  from "./components/publishing/publication-history-page";

// ── Brand-scoping (admin-assigned brands) ──────────────────────────────────
import { AdminBrandAccessPage } from "./components/login/admin/admin-brand-access-page";
import { AdminBrandKeysPage } from "./components/login/admin/admin-brand-keys-page";
import { GlobalSettingsPage } from "./components/admin/global-settings-page";
import { WindTunnelPage } from "./components/admin/wind-tunnel/wind-tunnel-page";
import { BrandRecordPage } from "./components/admin/brand-record/brand-record-page";
import { FrameworkLibraryPage } from "./components/admin/framework-library/framework-library-page";
import { NoBrandsScreen }       from "./components/brand/no-brands-screen";
import { NoRunsScreen }         from "./components/brand/no-runs-screen";
import { BrandSelectPage }      from "./components/brand/brand-select-page";
import { BrandSwitcherModal }   from "./components/brand/brand-switcher-modal";
import { RunHistoryDialog }     from "./components/brand/run-history-dialog";
import { UpmodeLogo }           from "./components/login/upmode-logo";
import { resolveAiCollapsed, resolveAiCollapsedFromCache } from "./services/settings/assistant-default";
import {
  isBrandScopedUser,
  getMyBrands,
  switchActiveBrand,
  listBrandRuns,
  getBrandRun,
  type BrandRunSummary,
  type BrandSummary,
} from "./services/brand/brand-access-service";

type CognitionConfigState = {
  productQuery:        string;
  selectedProductName: string | null;
  selectedModels:      string[];
  uploadedFile:        File | null;
  productSpecRaw:      string;
  isConfigMinimized:   boolean;
};

const EMPTY_COGNITION_CONFIG: CognitionConfigState = {
  productQuery: "", selectedProductName: null, selectedModels: [],
  uploadedFile: null, productSpecRaw: "", isConfigMinimized: false,
};

type AppPage =
  | "login" | "signup" | "terms" | "privacy"
  | "mfa-setup" | "mfa-verify"
  | "brand-search" | "dashboard" | "cognition"
  | "analytics-overview" | "fix-actions" | "ai-sandbox"
  | "brand-tools" | "report-generator"
  // The two surfaces the Insights & Reports redesign collapsed. They keep
  // resolving so a link or a bookmark to either lands on the phase it meant,
  // rather than on a dead route. See insights-reports-page.tsx.
  | "insights" | "report-history"
  | "settings" | "data-uploads" | "prompt-tracking"
  | "integrations" | "admin-users" | "admin-brand-access" | "admin-brand-keys"
  | "global-settings"
  | "wind-tunnel"
  | "brand-record"
  | "framework-library"
  | "agents"
  | "agent-runs"
  | "upmode-agent"
  | "deep-signal"
  | "brand-publisher"
  | "publication-history"
  | "onboarding";

const APP_PAGES: AppPage[] = [
  "login", "signup", "terms", "privacy",
  "mfa-setup", "mfa-verify",
  "brand-search",
  "dashboard", "cognition", "analytics-overview", "fix-actions",
  "ai-sandbox", "brand-tools", "report-generator", "insights", "report-history",
  "settings", "data-uploads", "prompt-tracking", "integrations",
  "admin-users", "admin-brand-access", "admin-brand-keys", "global-settings", "wind-tunnel", "brand-record", "framework-library", "agents", "agent-runs",
  "upmode-agent",
  "brand-publisher",
  "publication-history",
  "onboarding",
  "deep-signal",
];

function isAppPage(p: string): p is AppPage {
  return (APP_PAGES as string[]).includes(p);
}

// ── Auth storage helpers ──────────────────────────────────────────────────
function getStoredToken() { return localStorage.getItem("access_token")  || sessionStorage.getItem("access_token"); }
function getIsAdmin()     { return localStorage.getItem("is_admin") === "true" || sessionStorage.getItem("is_admin") === "true"; }
function getStoredUsername() { return localStorage.getItem("username") || sessionStorage.getItem("username") || ""; }

// Forced first-login password change. Persisted so a page reload while the
// gate is up keeps gating; set at login (from /auth/me) and cleared once the
// user sets their own password.
const MUST_CHANGE_KEY = "must_change_password";
function getStoredMustChange() {
  return localStorage.getItem(MUST_CHANGE_KEY) === "true"
      || sessionStorage.getItem(MUST_CHANGE_KEY) === "true";
}
function setStoredMustChange(value: boolean, rememberMe = true) {
  const store = rememberMe ? localStorage : sessionStorage;
  const other = rememberMe ? sessionStorage : localStorage;
  other.removeItem(MUST_CHANGE_KEY);
  if (value) store.setItem(MUST_CHANGE_KEY, "true");
  else store.removeItem(MUST_CHANGE_KEY);
}
function getUserType()    { return localStorage.getItem("user_type") || sessionStorage.getItem("user_type") || "basic"; }
function getCachedNav()   { try { return localStorage.getItem("up_pref_nav_collapsed") === "true"; } catch { return false; } }
// The assistant is CLOSED by default (UPM-152 phase 12b). A stored preference
// wins in either direction, so anyone who wants it open sets it once and it
// stays. Absent a preference the panel starts collapsed: it opens from the
// floating pill, the keyboard shortcut, or any contextual "Ask about this
// page" / insight deep-link, which open it WITH that context loaded.
function getCachedAi()    {
  try {
    return resolveAiCollapsedFromCache(localStorage.getItem("up_pref_ai_collapsed"));
  } catch { return true; }
}

function hasAgentAccess() {
  const t = getUserType();
  return t === "agent" || t === "admin" || getIsAdmin();
}

/**
 * Pages that render in their own full-bleed shell — no left navigation,
 * no left margin reserved, no mobile-hamburger top padding. The shared
 * SiteHeader is rendered above these (see showSiteHeader) so the Upmode
 * wordmark, the admin Tools menu (Home lives in it) and Account are always
 * reachable.
 *
 * brand-search renders the SiteHeader + hero on its own (inside
 * upmode-home.tsx) so it is intentionally NOT given a second one here.
 */
const FULL_BLEED_PAGES: ReadonlySet<string> = new Set([
  "brand-search",
  "onboarding",
]);

interface AuthPayload {
  username:   string;
  is_admin:   boolean;
  user_type:  string;
  full_name?: string | null;
  rememberMe: boolean;
}

interface PendingMfaUser {
  username:   string;
  is_admin:   boolean;
  user_type:  string;
  full_name?: string | null;
  rememberMe: boolean;
}

/**
 * Persist the access token + user attributes in the appropriate storage.
 * "Remember me" → localStorage (survives browser restart).
 * Otherwise → sessionStorage (cleared when the tab closes).
 *
 * Always wipes the OTHER storage so we don't end up with two stale tokens.
 */
function persistAuth(token: string, payload: AuthPayload) {
  const store = payload.rememberMe ? localStorage : sessionStorage;
  const other = payload.rememberMe ? sessionStorage : localStorage;

  ["access_token", "is_admin", "user_type", "username", "full_name"].forEach((k) => {
    other.removeItem(k);
  });

  store.setItem("access_token", token);
  store.setItem("is_admin",     String(!!payload.is_admin));
  store.setItem("user_type",    payload.user_type || "basic");
  store.setItem("username",     payload.username);
  if (payload.full_name) store.setItem("full_name", payload.full_name);
}

/**
 * Models used when auto-loading Brand Monitoring for a brand-scoped user
 * (they have no brand-search screen to pick models on).
 * >>> Align this list with BrandSearchPage's default selection. <<<
 */
const DEFAULT_BRAND_MODELS: string[] = (() => {
  try {
    const m = JSON.parse(localStorage.getItem("bm_models") || "[]");
    if (Array.isArray(m) && m.length) return m as string[];
  } catch {}
  return ["gpt-4o", "claude-sonnet-4", "perplexity-sonar"];
})();

/** Human-readable run timestamp for the dashboard baseline label. */
function formatWhen(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

function AppInner() {
  const { isMobile } = useResponsive();
  // Per-feature entitlement states — the SAME source that shows/hides items in
  // the nav. Navigation is gated by these so only "live" features are reachable.
  const { states } = useEntitlements();

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!getStoredToken());
  const [mustChangePassword, setMustChangePassword] = useState<boolean>(() => getStoredMustChange());
  const [currentPage,     setCurrentPage]     = useState<AppPage>(isAuthenticated ? "brand-search" : "login");
  const [deepSignalInitialTab, setDeepSignalInitialTab] = useState<string | undefined>(undefined);

  const [brandName,      setBrandName]      = useState("");
  const [brandId,        setBrandId]        = useState<string>("");
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [isConfigured,   setIsConfigured]   = useState(false);
  const [antennaData,    setAntennaData]    = useState<any | null>(null);
  const [cognitionData,  setCognitionData]  = useState<any | null>(null);
  const [cognitionConfig,setCognitionConfig]= useState<CognitionConfigState>(EMPTY_COGNITION_CONFIG);
  const [isAntennaLoading, setIsAntennaLoading] = useState(false);
  const [fixActionsHighlightTitle, setFixActionsHighlightTitle] = useState<string | null>(null);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(getCachedAi);
  const [navCollapsedUI,     setNavCollapsedUI]     = useState(getCachedNav);

  // ── MFA pending-flow state ────────────────────────────────────────────
  const [mfaSetupToken,     setMfaSetupToken]     = useState<string | null>(null);
  const [mfaChallengeToken, setMfaChallengeToken] = useState<string | null>(null);
  const [mfaPendingUser,    setMfaPendingUser]    = useState<PendingMfaUser | null>(null);

  // ── Self-service signup ───────────────────────────────────────────────
  // Off until /auth/config says otherwise, so a deployment with registration
  // closed never shows a "Sign up" link that the server would 404. Failing
  // closed here costs nothing: the server is the actual control.
  const [registrationEnabled, setRegistrationEnabled] = useState(false);

  // ── SSO callback state ────────────────────────────────────────────────
  // True while we're exchanging an IdP auth code on this page load.
  const [ssoProcessing, setSsoProcessing] = useState<boolean>(hasSsoCallbackParams());
  const [ssoError,      setSsoError]      = useState<string | null>(null);

  // ── Brand-scope state ─────────────────────────────────────────────────
  const [brandScope, setBrandScope] = useState<{
    checked: boolean;
    brands: string[];
    summaries: BrandSummary[];
    active: string | null;
    noBrands: boolean;
    /** True once the user has explicitly picked a brand this session. Until
     *  then the dedicated brand-select screen is shown (no default auto-load). */
    chosen: boolean;
  }>({ checked: false, brands: [], summaries: [], active: null, noBrands: false, chosen: false });
  const [brandSwitcherOpen, setBrandSwitcherOpen] = useState(false);
  const [runHistoryOpen, setRunHistoryOpen] = useState(false);
  const [brandRuns, setBrandRuns]   = useState<BrandRunSummary[]>([]);
  const [activeRunId, setActiveRunId] = useState<number | null>(null);
  // Set when a brand-scoped user's active brand has zero runs yet.
  const [noRunsBrand, setNoRunsBrand] = useState<string | null>(null);

  const brandRunId: number | null = (antennaData as any)?.runId ?? null;

  const handleSidebarToggle = useCallback((collapsed: boolean) => {
    setIsSidebarCollapsed(collapsed);
    try { localStorage.setItem("up_pref_ai_collapsed", String(collapsed)); } catch {}
  }, []);

  const resetSession = useCallback(() => {
    setIsConfigured(false); setBrandName(""); setBrandId(""); setSelectedModels([]);
    setNavCollapsedUI(getCachedNav());
    setAntennaData(null); setCognitionData(null); setCognitionConfig(EMPTY_COGNITION_CONFIG);
    setIsAntennaLoading(false); setFixActionsHighlightTitle(null);
    handleSidebarToggle(getCachedAi());
    localStorage.removeItem("bm_brand");
    localStorage.removeItem("bm_models");
    localStorage.removeItem("bm_last_result");
  }, [handleSidebarToggle]);

  // ── Brand-scope: load last run (no auto re-run) ───────────────────────
  /** Load a specific run's saved data into the dashboard (no LLM call). */
  const loadBrandRun = useCallback(async (brand: string, runId: number) => {
    const run = await getBrandRun(runId, brand);
    const antenna: any = { ...(run.data || {}) };
    antenna.runId = run.id;
    if (run.created_at) antenna.baselineSnapshot = formatWhen(run.created_at);

    try {
      localStorage.setItem("bm_brand", brand);
      localStorage.setItem("bm_brand_id", String(run.id));
      localStorage.setItem("bm_models", JSON.stringify(run.selected_models || []));
      localStorage.setItem("bm_last_result", JSON.stringify(antenna));
    } catch {}

    setBrandName(brand);
    setBrandId(String(run.id));
    setSelectedModels((run.selected_models || []) as any);
    setAntennaData(antenna);
    setCognitionData(null);
    setCognitionConfig(EMPTY_COGNITION_CONFIG);
    setIsConfigured(true);
    setIsAntennaLoading(false);
    setActiveRunId(run.id);
    setNoRunsBrand(null);
    setCurrentPage("dashboard");
  }, []);

  /** Resolve a brand-scoped user into the dashboard: load the latest run,
   *  or show the "no analysis yet" screen. NEVER auto-runs an analysis. */
  const enterBrand = useCallback(async (brand: string) => {
    let runs: BrandRunSummary[] = [];
    try { runs = await listBrandRuns(brand); } catch { runs = []; }
    setBrandRuns(runs);

    if (runs.length) {
      try {
        setNoRunsBrand(null);
        await loadBrandRun(brand, runs[0].id);
      } catch {
        // Run row exists but its data failed to load — show the retryable
        // "no analysis yet" screen rather than bubbling to a "no brands" error.
        setBrandName(brand);
        setBrandId("");
        setSelectedModels(DEFAULT_BRAND_MODELS);
        setAntennaData(null);
        setIsConfigured(false);
        setIsAntennaLoading(false);
        setActiveRunId(null);
        setNoRunsBrand(brand);
      }
    } else {
      // Assigned brand with no monitoring runs — prompt, don't auto-run.
      setBrandName(brand);
      setBrandId("");
      setSelectedModels(DEFAULT_BRAND_MODELS);
      setAntennaData(null);
      setIsConfigured(false);
      setIsAntennaLoading(false);
      setActiveRunId(null);
      setNoRunsBrand(brand);
    }
  }, [loadBrandRun]);

  const bootstrapBrandScope = useCallback(async () => {
    try {
      const res = await getMyBrands();
      if (!res.brands.length) {
        setBrandScope({ checked: true, brands: [], summaries: [], active: null, noBrands: true, chosen: false });
        return;
      }
      // Do NOT auto-load a default brand. Surface the dedicated brand
      // picker (chosen=false) so the user explicitly chooses which brand
      // to load. Brands with no runs are shown disabled there.
      setBrandScope({
        checked: true,
        brands: res.brands,
        summaries: res.brand_summaries || [],
        active: res.active_brand || null,
        noBrands: false,
        chosen: false,
      });
    } catch {
      setBrandScope({ checked: true, brands: [], summaries: [], active: null, noBrands: true, chosen: false });
    }
  }, []);

  /** Dedicated brand-select screen: persist the chosen brand server-side,
   *  then load its latest run into the dashboard. */
  const handleSelectBrand = useCallback(async (brand: string) => {
    try { await switchActiveBrand(brand); } catch { /* non-fatal — load anyway */ }
    // Load the run (sets currentPage→dashboard) BEFORE flipping `chosen`,
    // so we never flash the brand-search page in the gap. The picker keeps
    // its own per-row spinner up while this awaits.
    await enterBrand(brand);
    setBrandScope((s) => ({ ...s, active: brand, chosen: true }));
  }, [enterBrand]);

  /** Called by BrandSwitcherModal after the active brand changes. */
  const handleBrandSwitched = useCallback(async (brand: string) => {
    setBrandScope((s) => ({ ...s, active: brand, chosen: true }));
    await enterBrand(brand);
  }, [enterBrand]);

  // Auto-load the brand when a brand-scoped user has exactly one assigned
  // brand — skip the picker entirely. Guarded so it fires once per bootstrap
  // (the ref resets whenever the scope is re-checked, e.g. after logout).
  // Browser tab title: "{Brand} · {Module} · Upmode" inside a module view; bare
  // pages (search / auth / loading) read "Upmode". Brand only rides with a module.
  useEffect(() => {
    const moduleLabel = moduleLabelForPage(currentPage);
    const brand = brandName || (typeof localStorage !== "undefined" ? localStorage.getItem("bm_brand") : "") || "";
    setPageTitle({ brand: moduleLabel ? brand : undefined, module: moduleLabel });
  }, [currentPage, brandName]);

  useEffect(() => {
    let cancelled = false;
    authApi.config()
      .then((cfg) => { if (!cancelled) setRegistrationEnabled(!!cfg.registration_enabled); })
      .catch(() => { /* leave signup hidden */ });
    return () => { cancelled = true; };
  }, []);

  const autoSelectedRef = useRef(false);
  useEffect(() => {
    if (!brandScope.checked) { autoSelectedRef.current = false; return; }
    if (brandScope.chosen || brandScope.noBrands) return;
    if (brandScope.brands.length === 1 && !autoSelectedRef.current) {
      autoSelectedRef.current = true;
      handleSelectBrand(brandScope.brands[0]);
    }
  }, [brandScope.checked, brandScope.chosen, brandScope.noBrands, brandScope.brands, handleSelectBrand]);

  /** Left-nav: load a different past run for the active brand. */
  const handleSelectBrandRun = useCallback(async (runId: number) => {
    const brand = brandScope.active || brandName;
    if (!brand) return;
    try { await loadBrandRun(brand, runId); }
    catch { /* swallow — stale run id */ }
  }, [brandScope.active, brandName, loadBrandRun]);

  /** First-ever run for an assigned brand (from the NoRuns screen). Uses the
   *  default model set and the existing WS run path in AntennaDashboard. */
  const handleStartFirstRun = useCallback(() => {
    const brand = noRunsBrand || brandScope.active || brandName;
    if (!brand) return;
    setNoRunsBrand(null);
    setBrandName(brand);
    setBrandId("");
    setSelectedModels(DEFAULT_BRAND_MODELS);
    setAntennaData(null);
    setCognitionData(null);
    setCognitionConfig(EMPTY_COGNITION_CONFIG);
    setIsConfigured(true);
    setIsAntennaLoading(true);
    setCurrentPage("dashboard");
  }, [noRunsBrand, brandScope.active, brandName]);

  /** Brand Monitoring "Re-run" — run again for THIS brand with chosen models.
   *  Drives the same WS path (antennaData=null ⇒ AntennaDashboard runs). */
  const handleRerun = useCallback((models: string[]) => {
    const brand = brandScope.active || brandName;
    if (!brand || !models.length) return;
    setSelectedModels(models as any);
    setBrandId("");
    setAntennaData(null);
    setCognitionData(null);
    setCognitionConfig(EMPTY_COGNITION_CONFIG);
    setIsConfigured(true);
    setIsAntennaLoading(true);
    setCurrentPage("dashboard");
  }, [brandScope.active, brandName]);

  /** Wrap antenna data updates: when a fresh run finishes, refresh the run
   *  list + active pointer so the left-nav history stays in sync. */
  const handleAntennaData = useCallback((data: any | null) => {
    setAntennaData(data);
    if (!data) return;
    const rid = (data as any)?.runId ?? null;
    if (rid != null) setActiveRunId(Number(rid));
    if (isBrandScopedUser()) {
      const brand = brandScope.active || brandName;
      if (brand) {
        listBrandRuns(brand).then(setBrandRuns).catch(() => {});
      }
    }
  }, [brandScope.active, brandName]);

  const clearMfaPending = useCallback(() => {
    setMfaSetupToken(null);
    setMfaChallengeToken(null);
    setMfaPendingUser(null);
  }, []);

  const applySettingsAfterAuth = useCallback(async () => {
    try {
      const s = await getSettings();
      applyUserSettingsToDom(s);
      const navPref = !!s.appearance?.startNavCollapsed;
      // Absent is NOT the same as false. `!!undefined` would force the
      // assistant open on every login and overwrite the closed-by-default
      // rule (phase 12b). Only an explicit stored boolean wins.
      const aiPref  = resolveAiCollapsed(s.appearance?.startAiCollapsed);
      localStorage.setItem("up_pref_nav_collapsed", String(navPref));
      localStorage.setItem("up_pref_ai_collapsed",  String(aiPref));
      setNavCollapsedUI(navPref); setIsSidebarCollapsed(aiPref);
    } catch {}
  }, []);

  const handleConfigure = (brand: string, models: string[]) => {
    setBrandName(brand); setBrandId(""); setSelectedModels(models);
    setIsConfigured(true); setIsAntennaLoading(true); setCurrentPage("dashboard");
    setAntennaData(null); setCognitionData(null); setCognitionConfig(EMPTY_COGNITION_CONFIG);
  };

  const handleLoadFromHistory = (payload: { brandName: string; selectedModels: string[]; antennaData: any }) => {
    setBrandName(payload.brandName); setSelectedModels(payload.selectedModels); setAntennaData(payload.antennaData);
    const rid = (payload.antennaData as any)?.runId ?? (payload.antennaData as any)?.id ?? localStorage.getItem("bm_brand_id");
    setBrandId(rid ? String(rid) : "");
    setIsConfigured(true); setIsAntennaLoading(false); setCurrentPage("dashboard");
    setCognitionData(null); setCognitionConfig(EMPTY_COGNITION_CONFIG);
  };

  const handleNavigate = useCallback((page: string, highlightActionTitle?: string) => {
    if (!isAppPage(page)) return;

    // Brand-scoped users have no brand-search / onboarding access.
    if (isBrandScopedUser() && (page === "brand-search" || page === "onboarding")) {
      setCurrentPage("dashboard");
      return;
    }
    if ((page === "admin-brand-access" || page === "admin-brand-keys" || page === "global-settings" || page === "wind-tunnel" || page === "brand-record" || page === "framework-library") && !getIsAdmin()) {
      setCurrentPage("dashboard");
      return;
    }

    // Feature-page route guard — gated by the user's entitlement states, the
    // same source that shows/hides items in the nav. Only "live" features are
    // navigable; "locked"/"hidden" bounce to dashboard. Admins are all-live.
    const feature = featureForPage(page);
    if (feature && stateOf(states, feature.id) !== "live") {
      setCurrentPage("dashboard");
      return;
    }

    if (page === "admin-users" && !getIsAdmin())     { setCurrentPage("dashboard"); return; }
    if (page === "onboarding"  && !getIsAdmin())     { setCurrentPage("brand-search"); return; }
    if (page === "agents"      && !hasAgentAccess()) { setCurrentPage("dashboard"); return; }
    if (page === "agent-runs"  && !hasAgentAccess()) { setCurrentPage("dashboard"); return; }
    if (page === "brand-search") { resetSession(); setCurrentPage("brand-search"); return; }
    if (page !== "dashboard") setIsAntennaLoading(false);
    if (page === "fix-actions" && highlightActionTitle) setFixActionsHighlightTitle(highlightActionTitle);
    if (page === "deep-signal") setDeepSignalInitialTab(highlightActionTitle);
    setCurrentPage(page);
  }, [resetSession, states]);

  /**
   * Dev-only navigation seam for the design sweep.
   *
   * The product has no URL routing: `currentPage` is state and the only way
   * into a screen is a click. That makes a screenshot sweep of 29 routes a
   * pile of brittle click recipes, and it makes `data-uploads` — which no
   * component navigates to — unreachable at all.
   *
   * So a screen can be asked for by name. It goes through `handleNavigate`,
   * which means every entitlement and admin guard still applies: a route that
   * bounces to the dashboard for a real user bounces here too. Compiled out
   * of the production bundle by `import.meta.env.DEV`.
   */
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const onAsk = (e: Event) => {
      const detail = (e as CustomEvent<{ page?: string; arg?: string }>).detail;
      if (detail?.page) handleNavigate(detail.page, detail.arg);
    };
    window.addEventListener("upmode:navigate", onAsk);
    return () => window.removeEventListener("upmode:navigate", onAsk);
  }, [handleNavigate]);

  // Collapsed is owned here so the main column animates against it, and is
  // persisted per user (UPM-140).
  const openActionsCount = useOpenActionsCount(brandId || localStorage.getItem("bm_brand_id") || undefined);

  const handleNavCollapsedChange = useCallback((next: boolean) => {
    setNavCollapsedUI(next);
    try { localStorage.setItem("up_pref_nav_collapsed", String(next)); } catch { /* ignore */ }
  }, []);

  const handleOpenCommandPalette = () => {
    window.dispatchEvent(new KeyboardEvent("keydown", {
      key: "/", code: "Slash", keyCode: 191, which: 191, bubbles: true, cancelable: true,
    }));
  };

  useEffect(() => {
    const token = getStoredToken();
    if (token) {
      setIsAuthenticated(true); applySettingsAfterAuth();
      if (isBrandScopedUser()) {
        bootstrapBrandScope();
      } else if (["login","signup","terms","privacy","mfa-setup","mfa-verify"].includes(currentPage)) {
        setCurrentPage("brand-search");
      }
    } else {
      setIsAuthenticated(false);
      if (!["login","signup","terms","privacy","mfa-setup","mfa-verify"].includes(currentPage)) {
        setCurrentPage("login");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Login / MFA flow handlers ─────────────────────────────────────────

  /**
   * Called by LoginPage (mfa_state === "none"), MfaSetupPage (after enroll),
   * and MfaVerifyPage (after challenge passes). All three paths reach here
   * with a fully-valid access token + user payload.
   */
  const handleLoginSuccess = useCallback(async (token: string, payload: AuthPayload) => {
    persistAuth(token, payload);
    clearMfaPending();
    setIsAuthenticated(true);

    // Authoritatively check (via /me) whether this account is still on an
    // admin-set/default credential and must set its own password first. All
    // three login paths (none / setup / challenge) funnel through here, so
    // this one check covers them all.
    try {
      const me = await authApi.getMe();
      if (me.must_change_password) {
        setStoredMustChange(true, payload.rememberMe);
        setMustChangePassword(true);
        return;  // the forced-set-password gate takes over; no brand bootstrap
      }
    } catch {
      // A transient /me failure shouldn't block sign-in — treat as no gate.
    }
    setStoredMustChange(false, payload.rememberMe);
    setMustChangePassword(false);

    await applySettingsAfterAuth();
    if (isBrandScopedUser()) {
      await bootstrapBrandScope();
    } else {
      setCurrentPage("brand-search");
    }
  }, [applySettingsAfterAuth, clearMfaPending, bootstrapBrandScope]);

  /** The user just set their own password on the forced-set gate. Drop the
   *  flag and continue into the app on the same (still-valid) session. */
  const handleInitialPasswordDone = useCallback(async () => {
    setStoredMustChange(false);
    setMustChangePassword(false);
    await applySettingsAfterAuth();
    if (isBrandScopedUser()) {
      await bootstrapBrandScope();
    } else {
      setCurrentPage("brand-search");
    }
  }, [applySettingsAfterAuth, bootstrapBrandScope]);

  /**
   * Login succeeded but the user has never enrolled MFA. Route them to the
   * setup page where they'll scan the QR code and confirm a TOTP code.
   */
  /**
   * On first load after the IdP redirects back (URL has ?code=&state=),
   * exchange the code for our app token and complete login. SSO users have
   * MFA enforced by the IdP, so the response is always mfa_state="none".
   */
  useEffect(() => {
    if (!hasSsoCallbackParams()) return;
    let alive = true;
    (async () => {
      try {
        const res = await completeSsoCallback();
        if (!alive) return;
        if (res.mfa_state !== "none" || !res.access_token) {
          throw new Error("SSO did not return a valid session.");
        }
        await handleLoginSuccess(res.access_token, {
          username:   res.username,
          is_admin:   !!res.is_admin,
          user_type:  res.user_type || "basic",
          full_name:  res.full_name,
          rememberMe: true,
        });
      } catch (err: any) {
        if (alive) setSsoError(err?.message || "SSO sign-in failed. Please try again.");
      } finally {
        if (alive) setSsoProcessing(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleLoginSuccess]);

  const handleMfaSetupRequired = useCallback((setupToken: string, payload: PendingMfaUser) => {
    setMfaSetupToken(setupToken);
    setMfaChallengeToken(null);
    setMfaPendingUser(payload);
    setCurrentPage("mfa-setup");
  }, []);

  /**
   * Login succeeded and the user is enrolled — they need to enter a TOTP
   * (or a backup code) to complete sign-in.
   */
  const handleMfaChallenge = useCallback((challengeToken: string, payload: PendingMfaUser) => {
    setMfaChallengeToken(challengeToken);
    setMfaSetupToken(null);
    setMfaPendingUser(payload);
    setCurrentPage("mfa-verify");
  }, []);

  /** "Cancel" / back from the MFA verify page → go back to the login screen. */
  const handleMfaCancel = useCallback(() => {
    clearMfaPending();
    setCurrentPage("login");
  }, [clearMfaPending]);

  /**
   * Existing signup flow. NOTE: with the new backend, signup creates a
   * "basic" user with mfa_required=true. The server's /auth/register no
   * longer returns an access token, so for signup-then-auto-login to work,
   * SignupPage should call /auth/login after register and route through the
   * same MFA flow as LoginPage. If your SignupPage hasn't been updated yet,
   * the safest behavior is to redirect to the login page after signup so
   * the user goes through the standard MFA-aware login path.
   */
  const handleSignupSuccess = async () => {
    setIsAuthenticated(true);
    await applySettingsAfterAuth();
    if (isBrandScopedUser()) {
      await bootstrapBrandScope();
    } else {
      setCurrentPage("brand-search");
    }
  };

  const handleLogout = useCallback(() => {
    [
      "access_token", "is_admin", "user_type",
      "username", "full_name",
      "up_pref_nav_collapsed", "up_pref_ai_collapsed",
      MUST_CHANGE_KEY,
    ].forEach((k) => {
      localStorage.removeItem(k);
      sessionStorage.removeItem(k);
    });

    setIsAuthenticated(false);
    setMustChangePassword(false);
    clearMfaPending();
    resetSession();
    setBrandScope({ checked: false, brands: [], summaries: [], active: null, noBrands: false, chosen: false });
    setBrandSwitcherOpen(false);
    setRunHistoryOpen(false);
    setBrandRuns([]);
    setActiveRunId(null);
    setNoRunsBrand(null);
    setCurrentPage("login");
  }, [resetSession, clearMfaPending]);

  // Any API call that hits 401 fires AUTH_EXPIRED_EVENT (see
  // src/api/api-fetch.ts). Treat it as an implicit logout so the user
  // lands on the login screen instead of staring at a half-rendered
  // page with a stale token. Only acts while authenticated — a 401 on
  // the public /auth/login endpoint shouldn't bounce the user around.
  useEffect(() => {
    const onExpired = () => {
      if (isAuthenticated) handleLogout();
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, [isAuthenticated, handleLogout]);

  // ── Unauthenticated render branch ─────────────────────────────────────
  if (!isAuthenticated) {
    // Exchanging an SSO auth code — show a spinner until it resolves.
    if (ssoProcessing && !ssoError) {
      return (
        <div style={{
          height: "100vh", display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", gap: 16,
          backgroundColor: "var(--background)", color: "var(--muted-foreground)",
          fontFamily: "var(--font-sans)",
        }}>
          <div className="animate-spin" style={{
            width: 28, height: 28, borderRadius: "50%",
            border: "3px solid var(--border)", borderTopColor: "var(--alpha-teal)",
          }} />
          Completing sign-in…
        </div>
      );
    }

    if (currentPage === "terms")   return <TermsOfService onNavigateBack={() => setCurrentPage("login")} />;
    if (currentPage === "privacy") return <PrivacyPolicy  onNavigateBack={() => setCurrentPage("login")} />;

    if (currentPage === "mfa-setup" && mfaSetupToken && mfaPendingUser) {
      return (
        <MfaSetupPage
          setupToken={mfaSetupToken}
          username={mfaPendingUser.username}
          rememberMe={mfaPendingUser.rememberMe}
          onSetupComplete={(token, p) =>
            handleLoginSuccess(token, {
              username:   p.username,
              is_admin:   p.is_admin,
              user_type:  p.user_type,
              full_name:  p.full_name,
              rememberMe: mfaPendingUser.rememberMe,
            })
          }
        />
      );
    }

    if (currentPage === "mfa-verify" && mfaChallengeToken && mfaPendingUser) {
      return (
        <MfaVerifyPage
          challengeToken={mfaChallengeToken}
          username={mfaPendingUser.username}
          onVerifySuccess={(token, p) =>
            handleLoginSuccess(token, {
              username:   p.username,
              is_admin:   p.is_admin,
              user_type:  p.user_type,
              full_name:  p.full_name,
              rememberMe: mfaPendingUser.rememberMe,
            })
          }
          onCancel={handleMfaCancel}
        />
      );
    }

    if (currentPage === "signup" && registrationEnabled) return (
      <SignupPage
        onSignupSuccess={handleSignupSuccess}
        onNavigateToLogin={() => setCurrentPage("login")}
        onNavigateToTerms={() => setCurrentPage("terms")}
        onNavigateToPrivacy={() => setCurrentPage("privacy")}
      />
    );

    return (
      <LoginPage
        onLoginSuccess={handleLoginSuccess}
        onMfaSetupRequired={handleMfaSetupRequired}
        onMfaChallenge={handleMfaChallenge}
        onNavigateToSignup={registrationEnabled ? () => setCurrentPage("signup") : undefined}
        onNavigateToTerms={() => setCurrentPage("terms")}
        onNavigateToPrivacy={() => setCurrentPage("privacy")}
        initialError={ssoError}
      />
    );
  }

  // ── Forced first-login password set ───────────────────────────────────
  // Authenticated but still on a temporary/admin-set credential — everything
  // else waits behind this until the user picks their own password.
  if (mustChangePassword) {
    return (
      <ForceSetPasswordPage
        username={getStoredUsername()}
        onDone={handleInitialPasswordDone}
        onLogout={handleLogout}
      />
    );
  }

  // ── Brand-scope gate (non-admin users) ────────────────────────────────
  if (isBrandScopedUser()) {
    // Show the loading screen while the scope is being checked, and also while
    // a single-brand user is being auto-loaded into their only brand — so the
    // picker never flashes in the gap before `chosen` flips.
    const autoLoadingSingleBrand =
      brandScope.checked && !brandScope.chosen && !brandScope.noBrands && brandScope.brands.length === 1;
    if (!brandScope.checked || autoLoadingSingleBrand) {
      return (
        <div className="min-h-screen flex flex-col bg-background">
          <div
            style={{
              padding: "16px 20px",
              borderBottom: "1px solid var(--border)",
              backgroundColor: "var(--card)",
            }}
          >
            <UpmodeLogo />
          </div>
          <div className="flex-1 flex items-center justify-center">
            <div className="flex items-center gap-3 text-muted-foreground">
              <div
                className="animate-spin"
                style={{
                  width: 18, height: 18, borderRadius: "50%",
                  border: "2px solid var(--border)",
                  borderTopColor: "var(--alpha-teal)",
                }}
              />
              <span style={{ fontSize: 14 }}>Loading your workspace…</span>
            </div>
          </div>
        </div>
      );
    }
    if (brandScope.noBrands) {
      return (
        <NoBrandsScreen
          fullName={localStorage.getItem("full_name") || sessionStorage.getItem("full_name")}
          onRetry={bootstrapBrandScope}
          onLogout={handleLogout}
        />
      );
    }
    // No default auto-load: ask which brand to open first. Brands without
    // a monitoring run are shown disabled in the picker.
    if (!brandScope.chosen) {
      return (
        <BrandSelectPage
          brands={brandScope.summaries}
          activeBrand={brandScope.active}
          onSelect={handleSelectBrand}
          onRetry={bootstrapBrandScope}
          onLogout={handleLogout}
          fullName={localStorage.getItem("full_name") || sessionStorage.getItem("full_name")}
        />
      );
    }
    if (noRunsBrand) {
      return (
        <NoRunsScreen
          brand={noRunsBrand}
          onRun={handleStartFirstRun}
          onRetry={() => enterBrand(noRunsBrand)}
          onLogout={handleLogout}
        />
      );
    }
  }

  // ── Authenticated render branch ───────────────────────────────────────
  const isFullBleedPage = FULL_BLEED_PAGES.has(currentPage);

  // The left nav appears only when we're in the "branded" flow (post-search)
  // AND the current page isn't one of the full-bleed shells (brand-search,
  // onboarding).
  const showLeftNav = isConfigured && !isAntennaLoading && !isFullBleedPage;
  const activeBrand = brandName || localStorage.getItem("bm_brand") || "";

  // On mobile, the nav becomes an overlay drawer — content does NOT shift right.
  // On tablet/desktop, reserve left margin equal to the sidebar width.
  const contentMarginLeft =
    !showLeftNav || isMobile
      ? "0"
      : navCollapsedUI ? "76px" : "280px";

  // Reserve ~56px top padding on mobile so the fixed hamburger button
  // doesn't visually overlap page headers.
  const contentPaddingTop = showLeftNav && isMobile ? "56px" : "0";

  // Content pages that render WITHOUT the left nav still get the shared
  // SiteHeader — the same header the landing page (UpmodeHome) uses, so the
  // admin sections are visually continuous with the product's front door.
  // brand-search is excluded because UpmodeHome renders SiteHeader itself.
  const showSiteHeader =
    !showLeftNav &&
    (currentPage === "admin-users" ||
      currentPage === "admin-brand-access" ||
      currentPage === "admin-brand-keys" ||
      currentPage === "global-settings" ||
      currentPage === "wind-tunnel" ||
      currentPage === "brand-record" ||
      currentPage === "framework-library" ||
      currentPage === "onboarding");

  return (
    // overflow-x-CLIP, not hidden. `overflow-x: hidden` forces the other axis
    // from `visible` to `auto`, which quietly made this div a scroll container:
    // a second vertical scrollport nested in the document's, and the nearest
    // scrollport for every `position: sticky` inside the app — which is why a
    // sticky element here scrolls away instead of sticking. `clip` gives the
    // same horizontal-overflow protection without creating a scrollport.
    <div className="relative min-h-screen bg-background overflow-x-clip">
      <BrandSwitcherModal
        open={brandSwitcherOpen}
        onClose={() => setBrandSwitcherOpen(false)}
        onBrandSwitched={handleBrandSwitched}
      />

      <RunHistoryDialog
        open={runHistoryOpen}
        onClose={() => setRunHistoryOpen(false)}
        brand={brandScope.active || brandName}
        runs={brandRuns}
        activeRunId={activeRunId ?? undefined}
        onSelect={handleSelectBrandRun}
      />

      {showLeftNav && (
        <NavRail
          currentPage={currentPage}
          onNavigate={handleNavigate}
          onLogout={handleLogout}
          collapsed={navCollapsedUI}
          onCollapsedChange={handleNavCollapsedChange}
          activeBrandName={activeBrand || undefined}
          onOpenCommandPalette={handleOpenCommandPalette}
          onOpenBrandSwitcher={isBrandScopedUser() ? () => setBrandSwitcherOpen(true) : undefined}
          openActionsCount={openActionsCount}
          railState={activeBrand ? undefined : "empty"}
        />
      )}

      <div
        className="transition-all duration-300 ease-in-out min-h-screen"
        style={{
          marginLeft: contentMarginLeft,
          paddingTop: contentPaddingTop,
        }}
      >
        {/* Pages opened without the left nav (admin-users, admin-brand-access,
            onboarding) render the shared SiteHeader so the Upmode wordmark,
            the admin Tools menu (which carries Home) and Account are always
            reachable — identical to the landing page. */}
        {showSiteHeader && (
          <SiteHeader
            currentPage={currentPage}
            onNavigate={handleNavigate}
            onLogout={handleLogout}
          />
        )}

        {currentPage === "brand-search" && (
          <UpmodeHome
            onConfigure={handleConfigure}
            onLoadFromHistory={handleLoadFromHistory}
            isSidebarCollapsed={isSidebarCollapsed}
            onSidebarToggle={handleSidebarToggle}
            onLogout={handleLogout}
            onNavigate={handleNavigate}
          />
        )}

        {currentPage === "onboarding" && (
          <OnboardingPage
            isSidebarCollapsed={isSidebarCollapsed}
            onSidebarToggle={handleSidebarToggle}
          />
        )}

        {currentPage === "dashboard" && (
          <AntennaDashboard
            isSidebarCollapsed={isSidebarCollapsed} onSidebarToggle={handleSidebarToggle}
            brandName={brandName} selectedModels={selectedModels as any}
            antennaData={antennaData} onAntennaData={handleAntennaData}
            onLoadingChange={setIsAntennaLoading} onNavigate={handleNavigate}
            onRerun={isBrandScopedUser() ? handleRerun : undefined}
          />
        )}

        {currentPage === "cognition" && (
          <CognitionAnalysis brandName={brandName} isSidebarCollapsed={isSidebarCollapsed}
            onSidebarToggle={handleSidebarToggle} onNavigate={handleNavigate}
            cognitionData={cognitionData} onCognitionData={setCognitionData}
            cognitionConfig={cognitionConfig} onCognitionConfigChange={setCognitionConfig} />
        )}

        {currentPage === "analytics-overview" && (
          <AnalyticsOverview isSidebarCollapsed={isSidebarCollapsed} onSidebarToggle={handleSidebarToggle}
            antennaData={antennaData} brandName={brandName} selectedModels={selectedModels} onNavigate={handleNavigate} />
        )}

        {currentPage === "fix-actions" && (
          <FixActionsPage isSidebarCollapsed={isSidebarCollapsed} onSidebarToggle={handleSidebarToggle}
            onNavigate={handleNavigate} brandName={brandName}
            brandId={brandId || localStorage.getItem("bm_brand_id") || ""}
            selectedModels={selectedModels} antennaData={antennaData} cognitionData={cognitionData}
            initialHighlightActionTitle={fixActionsHighlightTitle}
            onHighlightConsumed={() => setFixActionsHighlightTitle(null)} />
        )}

        {currentPage === "ai-sandbox" && (
          <AISandboxPage
            isSidebarCollapsed={isSidebarCollapsed}
            onSidebarToggle={handleSidebarToggle}
            antennaData={antennaData}
          />
        )}

        {currentPage === "deep-signal" && (
          <DeepSignalPage
            isSidebarCollapsed={isSidebarCollapsed}
            onSidebarToggle={handleSidebarToggle}
            brandName={brandName}
            onNavigate={handleNavigate}
            initialTab={deepSignalInitialTab}
          />
        )}

        {currentPage === "brand-tools" && (
          <BrandVoiceProfile isSidebarCollapsed={isSidebarCollapsed} onSidebarToggle={handleSidebarToggle} />
        )}

        {/* One page, three phases. `insights` and `report-history` are the two
            collapsed routes and they open the phase they used to be. */}
        {(currentPage === "report-generator" ||
          currentPage === "insights" ||
          currentPage === "report-history") && (
          <InsightsReportsPage brandName={brandName} brandRunId={brandRunId}
            initialPhase={currentPage === "report-history" ? "reports" : "library"}
            isSidebarCollapsed={isSidebarCollapsed} onSidebarToggle={handleSidebarToggle} />
        )}

        {currentPage === "settings" && (
          <SettingsPage isSidebarCollapsed={isSidebarCollapsed} onSidebarToggle={handleSidebarToggle} />
        )}

        {currentPage === "data-uploads" && (
          <DataUploadsPage isSidebarCollapsed={isSidebarCollapsed} onSidebarToggle={handleSidebarToggle} />
        )}

        {currentPage === "prompt-tracking" && (
          <PromptTrackingPage isSidebarCollapsed={isSidebarCollapsed} onSidebarToggle={handleSidebarToggle} />
        )}

        {currentPage === "integrations" && (
          <IntegrationsPage isSidebarCollapsed={isSidebarCollapsed} onSidebarToggle={handleSidebarToggle} activeBrandName={activeBrand}/>
        )}
        {currentPage === "brand-publisher" && (
          <BrandPublisherPage
            activeBrandName={activeBrand}
            onNavigate={handleNavigate}
          />
        )}

        {currentPage === "publication-history" && (
          <PublicationHistoryPage
            activeBrandName={activeBrand}
          />
        )}

        {currentPage === "admin-users" && <AdminUsersPage />}

        {currentPage === "admin-brand-access" && <AdminBrandAccessPage />}

        {currentPage === "admin-brand-keys" && <AdminBrandKeysPage />}

        {currentPage === "global-settings" && <GlobalSettingsPage onNavigate={handleNavigate} />}

        {currentPage === "wind-tunnel" && <WindTunnelPage />}

        {currentPage === "brand-record" && <BrandRecordPage />}

        {currentPage === "framework-library" && <FrameworkLibraryPage />}

        {currentPage === "agent-runs" && (
          <AgentRunsPage
            isSidebarCollapsed={isSidebarCollapsed}
            onSidebarToggle={handleSidebarToggle}
            activeBrandName={activeBrand}
          />
        )}

        {currentPage === "agents" && (
          <AgentsPage
            isSidebarCollapsed={isSidebarCollapsed}
            onSidebarToggle={handleSidebarToggle}
            activeBrandName={activeBrand}
          />
        )}

        {currentPage === "upmode-agent" && (
          <UpmodeAgentPage
            isSidebarCollapsed={isSidebarCollapsed}
            onSidebarToggle={handleSidebarToggle}
            activeBrandName={activeBrand}
          />
        )}
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ErrorBoundary>
          <AppInner />
          <Toaster position="top-right" />
        </ErrorBoundary>
      </AuthProvider>
    </ThemeProvider>
  );
}